import { useCallback, useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { SuccessFetchListResponse } from "@/services/api";
import { costsService } from "@/services/costs";
import { ReadCosts } from "@/types/costs";
import { UseDataTableOptions } from "@/types/dataTableSettings";

export function useInvoiceSpecificationCostsQuery(
  invoiceSpecificationId: string,
  options: UseDataTableOptions<ReadCosts>,
) {
  const queryKey = useDynamicQueryParams("costs", options);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      costsService.getList(invoiceSpecificationId, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [invoiceSpecificationId, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadCosts>) => lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  return { query, queryValue };
}
